import type { ArchetypeId } from '@llura/types';
import type { ArchetypeKey } from '@llura/constants';
import type { ComponentType, SVGProps } from 'react';
import {
  EnchantressGlyph,
  HuntressGlyph,
  PriestessGlyph,
  QueenGlyph,
  SensualistGlyph,
} from './archetype-icons';

export interface ArchetypeColor {
  /** HSL triplet, e.g. `271 45% 32%`, suitable for `hsl(var(--primary))`. */
  primary: string;
  secondary: string;
  accent: string;
  /** Glow color — defaults to primary when set equal. */
  glow: string;
}

export interface BreathRhythmMs {
  inhale: number;
  hold: number;
  exhale: number;
}

export interface ArchetypeDefinition {
  id: ArchetypeId;
  /** Lookup key into `strings.archetypes[...]`. */
  i18nKey: ArchetypeKey;
  color: ArchetypeColor;
  breathRhythmMs: BreathRhythmMs;
  Icon: ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;
}

const sec = (n: number) => n * 1000;

export const ARCHETYPES: readonly ArchetypeDefinition[] = [
  {
    id: 'priestess',
    i18nKey: 'priestess',
    color: {
      primary: '271 45% 32%',
      secondary: '243 55% 55%',
      accent: '260 50% 75%',
      glow: '271 45% 32%',
    },
    breathRhythmMs: { inhale: sec(4), hold: sec(7), exhale: sec(8) },
    Icon: PriestessGlyph,
  },
  {
    id: 'sensualist',
    i18nKey: 'sensualist',
    color: {
      primary: '18 55% 70%',
      secondary: '38 38% 78%',
      accent: '30 45% 85%',
      glow: '18 55% 70%',
    },
    breathRhythmMs: { inhale: sec(5), hold: sec(2), exhale: sec(6) },
    Icon: SensualistGlyph,
  },
  {
    id: 'huntress',
    i18nKey: 'huntress',
    color: {
      primary: '355 65% 45%',
      secondary: '18 75% 50%',
      accent: '28 70% 65%',
      glow: '355 65% 45%',
    },
    breathRhythmMs: { inhale: sec(3), hold: 0, exhale: sec(3) },
    Icon: HuntressGlyph,
  },
  {
    id: 'queen',
    i18nKey: 'queen',
    color: {
      primary: '325 38% 38%',
      secondary: '42 60% 60%',
      accent: '38 38% 78%',
      glow: '325 38% 38%',
    },
    breathRhythmMs: { inhale: sec(4), hold: sec(4), exhale: sec(4) },
    Icon: QueenGlyph,
  },
  {
    id: 'enchantress',
    i18nKey: 'enchantress',
    color: {
      primary: '230 40% 18%',
      secondary: '0 0% 65%',
      accent: '260 10% 8%',
      glow: '230 40% 28%',
    },
    breathRhythmMs: { inhale: sec(6), hold: sec(4), exhale: sec(8) },
    Icon: EnchantressGlyph,
  },
] as const;

export const ARCHETYPE_IDS = ARCHETYPES.map((a) => a.id) as readonly ArchetypeId[];

const ARCHETYPES_BY_ID = new Map<ArchetypeId, ArchetypeDefinition>(
  ARCHETYPES.map((a) => [a.id, a]),
);

export function getArchetype(id: ArchetypeId): ArchetypeDefinition {
  const found = ARCHETYPES_BY_ID.get(id);
  if (!found) {
    throw new Error(`Unknown archetype id: ${id}`);
  }
  return found;
}

export function isArchetypeId(value: string): value is ArchetypeId {
  return ARCHETYPES_BY_ID.has(value as ArchetypeId);
}
